import {RecordsWebPage} from '@fe-progressive/package-progressive/records-web';
import '@fe-progressive/package-progressive/records-web/style.css';
import {
  type ProgressiveRecordItem,
  type ProgressiveRecordType,
  type ProgressiveRecordsAdapter,
  normalizeRecordStatus,
  normalizeWithdrawRecordStatus,
} from '@fe-progressive/package-progressive/records';
import globalStore from '@/services/global.state';
import {copyText, goBack, goCS, toPriceStr} from '@/utils';
import dayjs from 'dayjs';
import React from 'react';
import './records-progressive.scss';
import {
  getRechargeLogs,
  getTransferLogs,
  getWithdrawLogs,
  type RechargeLogItem,
  type TransferLogItem,
  type WithdrawLogItem,
} from '../records/records-service';

function formatAmount(amount: number) {
  return toPriceStr(amount, {
    thousands: true,
    spacing: true,
    currency: globalStore.currency,
  });
}

function formatTime(time?: number) {
  return time ? dayjs(Number(time)).format('DD/MM/YYYY hh:mm') : '';
}

const RECORDS_PROGRESSIVE_VIEWPORT_HEIGHT_VAR =
  '--records-progressive-viewport-height';

function useRecordsProgressiveViewportHeightStyle() {
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const updateViewportHeight = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty(
        RECORDS_PROGRESSIVE_VIEWPORT_HEIGHT_VAR,
        `${height}px`,
      );
    };

    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('scroll', updateViewportHeight);

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener(
        'resize',
        updateViewportHeight,
      );
      window.visualViewport?.removeEventListener(
        'scroll',
        updateViewportHeight,
      );
      document.documentElement.style.removeProperty(
        RECORDS_PROGRESSIVE_VIEWPORT_HEIGHT_VAR,
      );
    };
  }, []);

  return React.useMemo(
    () => ({
      height: `var(${RECORDS_PROGRESSIVE_VIEWPORT_HEIGHT_VAR}, 100vh)`,
    }),
    [],
  );
}

function mapWithdraw(item: WithdrawLogItem): ProgressiveRecordItem {
  return {
    id: item.orderNo,
    type: 'withdraw',
    status: normalizeWithdrawRecordStatus(item.respCode),
    amount: item.paidAmount,
    reward: item.reward,
    fee: item.withdrawFeeRate * item.paidAmount,
    orderNo: item.orderNo,
    createdAt: item.appDate || item.createTime,
    balance: item.balance,
    bankSummary: {
      bankName: item.bankName,
      userCardNumber: item.userCardNumber,
    },
    raw: item,
  };
}

function mapRecharge(item: RechargeLogItem): ProgressiveRecordItem {
  return {
    id: item.orderNo,
    type: 'recharge',
    status: normalizeRecordStatus(item.status),
    amount: item.paidAmount,
    reward: item.reward,
    channelName: item.channelName,
    orderNo: item.orderNo,
    createdAt: item.appDate || item.createTime,
    balance: item.balance,
    raw: item,
  };
}

function mapTransfer(item: TransferLogItem): ProgressiveRecordItem {
  return {
    id: item.order,
    type: 'transfer',
    amount: item.price,
    reward: item.reward,
    orderNo: item.order,
    createdAt: item.appDate || item.createTime,
    balance: item.balance,
    raw: item,
  };
}

async function fetchRecords(
  type: ProgressiveRecordType,
  pageNo: number,
  pageSize: number,
) {
  if (type === 'withdraw') {
    return (await getWithdrawLogs(pageNo, pageSize)).map(mapWithdraw);
  }
  if (type === 'recharge') {
    return (await getRechargeLogs(pageNo, pageSize)).map(mapRecharge);
  }
  return (await getTransferLogs(pageNo, pageSize)).map(mapTransfer);
}

export default function RecordsProgressive(props: {
  type: ProgressiveRecordType;
}) {
  const {type} = props;
  const viewportHeightStyle = useRecordsProgressiveViewportHeightStyle();
  const adapter = React.useMemo<ProgressiveRecordsAdapter>(
    () => ({
      fetchRecords: ({pageNo, pageSize}) =>
        fetchRecords(type, pageNo, pageSize),
      capabilities: {
        enableCryptoRecords: false,
      },
      copyText: (text: string) => copyText(text),
      showToast: notice => globalStore.globalSucessTotal(notice.message),
    }),
    [type],
  );

  return (
    <div className="records-progressive-page" style={viewportHeightStyle}>
      <RecordsWebPage
        type={type}
        adapter={adapter}
        onBack={goBack}
        onCustomerService={goCS}
        formatAmount={formatAmount}
        formatTime={formatTime}
      />
    </div>
  );
}
